import React, { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Tabs, Tab, Button, TextField, Select, MenuItem,
  InputLabel, FormControl, Snackbar, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  getAllVendors, createVendor, updateVendor, deactivateVendor, reactivateVendor,
  getAllRides, assignVendor, cancelAssignment, updateAssignmentStatus,
  getAssignmentForBooking,
} from "../../services/api";

const VENDOR_TYPES = ["INDIVIDUAL", "FLEET", "AGGREGATOR_API", "OTHER"];
const CATEGORIES = ["RIDE", "IRON", "LAUNDRY"];
const STATUSES = ["ASSIGNED", "EN_ROUTE", "PICKED_UP", "COMPLETED", "CANCELLED", "FAILED"];

const initialVendorForm = {
  category: "RIDE",
  vendorName: "",
  vendorType: "INDIVIDUAL",
  contactPerson: "",
  contactNumber: "",
  email: "",
  vehicleNumber: "",
  licenseNumber: "",
};

export default function VendorAssignmentTest() {
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showMessage = (message, severity = "success") => {
    // Reset snackbar first to ensure new messages are visible immediately
    setSnackbar({ open: false, message: "", severity: "success" });
    setTimeout(() => {
      setSnackbar({ open: true, message, severity });
    }, 50);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h4" mb={2}>
        🛠️ Vendor & Assignment Testing
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Use this page to test all vendor and assignment endpoints.
      </Typography>

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Manage Vendors" />
          <Tab label="Assign to Booking" />
          <Tab label="Test Status Updates" />
        </Tabs>
      </Paper>

      <Box mt={3}>
        {tab === 0 && <VendorManagementTab onMessage={showMessage} />}
        {tab === 1 && <AssignmentTab onMessage={showMessage} />}
        {tab === 2 && <StatusUpdateTab onMessage={showMessage} />}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ============================================================
// TAB 1: VENDOR MANAGEMENT
// ============================================================
function VendorManagementTab({ onMessage }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialVendorForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVendors = async () => {
    setLoading(true);
    try {
      const res = await getAllVendors();
      setVendors(res.data.data || []);
    } catch (err) {
      onMessage("Failed to load vendors: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Generic field change handler — uses functional state update
  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Special handler for phone — digits only, max 10
  const handlePhoneChange = (e) => {
    const v = e.target.value.replace(/\D/g, "");
    if (v.length <= 10) {
      setForm((prev) => ({ ...prev, contactNumber: v }));
    }
  };

  const openCreateDialog = () => {
    setEditing(null);
    setForm({ ...initialVendorForm });
    setDialogOpen(true);
  };

  const openEditDialog = (vendor) => {
    setEditing(vendor);
    setForm({
      category: vendor.category || "RIDE",
      vendorName: vendor.vendorName || "",
      vendorType: vendor.vendorType || "INDIVIDUAL",
      contactPerson: vendor.contactPerson || "",
      contactNumber: vendor.contactNumber || "",
      email: vendor.email || "",
      vehicleNumber: vendor.vehicleNumber || "",
      licenseNumber: vendor.licenseNumber || "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm({ ...initialVendorForm });
  };

  const handleSubmit = async () => {
    // Trim values to handle whitespace-only inputs
    const vendorName = (form.vendorName || "").trim();
    const contactPerson = (form.contactPerson || "").trim();
    const contactNumber = (form.contactNumber || "").trim();
    const email = (form.email || "").trim();
    const vehicleNumber = (form.vehicleNumber || "").trim();
    const licenseNumber = (form.licenseNumber || "").trim();

    // Debug log — remove after confirming it works
    console.log("Submit - form state:", form);
    console.log("Submit - trimmed:", { vendorName, contactPerson, contactNumber });

    // Validation
    if (!vendorName) {
      onMessage("Vendor name is required", "error");
      return;
    }
    if (!contactPerson) {
      onMessage("Contact person is required", "error");
      return;
    }
    if (!contactNumber) {
      onMessage("Contact number is required", "error");
      return;
    }
    if (!/^\d{10}$/.test(contactNumber)) {
      onMessage("Contact number must be exactly 10 digits", "error");
      return;
    }

    // Prepare clean payload
    const payload = {
      category: form.category,
      vendorName,
      vendorType: form.vendorType,
      contactPerson,
      contactNumber,
      email: email || null,
      vehicleNumber: vehicleNumber || null,
      licenseNumber: licenseNumber || null,
    };

    setSubmitting(true);
    try {
      if (editing) {
        await updateVendor(editing.vendorId, payload);
        onMessage("Vendor updated successfully");
      } else {
        await createVendor(payload);
        onMessage("Vendor created successfully");
      }
      closeDialog();
      loadVendors();
    } catch (err) {
      onMessage(err.response?.data?.message || "Operation failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (vendor) => {
    try {
      if (vendor.isActive) {
        await deactivateVendor(vendor.vendorId);
        onMessage(`${vendor.vendorName} deactivated`);
      } else {
        await reactivateVendor(vendor.vendorId);
        onMessage(`${vendor.vendorName} reactivated`);
      }
      loadVendors();
    } catch (err) {
      onMessage(err.response?.data?.message || "Failed", "error");
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Vendors ({vendors.length})
        </Typography>
        <Box>
          <IconButton onClick={loadVendors} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" onClick={openCreateDialog} sx={{ ml: 1 }}>
            + Add Vendor
          </Button>
        </Box>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : vendors.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No vendors yet. Click "+ Add Vendor" to create one.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.map((v) => (
                <TableRow key={v.vendorId}>
                  <TableCell>{v.vendorId}</TableCell>
                  <TableCell><Chip label={v.category} size="small" /></TableCell>
                  <TableCell>{v.vendorName}</TableCell>
                  <TableCell><Chip label={v.vendorType} size="small" variant="outlined" /></TableCell>
                  <TableCell>{v.contactNumber}</TableCell>
                  <TableCell>{v.vehicleNumber || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={v.isActive ? "Active" : "Inactive"}
                      color={v.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => openEditDialog(v)} title="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleToggleActive(v)}
                      title={v.isActive ? "Deactivate" : "Reactivate"}
                      color={v.isActive ? "error" : "primary"}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Vendor" : "Add New Vendor"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={form.category}
                label="Category"
                onChange={handleChange("category")}
                disabled={!!editing}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Vendor Name *"
              value={form.vendorName}
              onChange={handleChange("vendorName")}
              fullWidth
              autoFocus
            />

            <FormControl fullWidth>
              <InputLabel>Vendor Type</InputLabel>
              <Select
                value={form.vendorType}
                label="Vendor Type"
                onChange={handleChange("vendorType")}
              >
                {VENDOR_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Contact Person *"
              value={form.contactPerson}
              onChange={handleChange("contactPerson")}
              fullWidth
            />

            <TextField
              label="Contact Number * (10 digits)"
              value={form.contactNumber}
              onChange={handlePhoneChange}
              fullWidth
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10,
              }}
            />

            <TextField
              label="Email (optional)"
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              fullWidth
            />

            <TextField
              label="Vehicle Number (optional)"
              value={form.vehicleNumber}
              onChange={handleChange("vehicleNumber")}
              fullWidth
            />

            <TextField
              label="License Number (optional)"
              value={form.licenseNumber}
              onChange={handleChange("licenseNumber")}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? "Saving..." : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================
// TAB 2: ASSIGN VENDOR TO BOOKING
// ============================================================
function AssignmentTab({ onMessage }) {
  const [bookings, setBookings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState({});

  const [assignDialog, setAssignDialog] = useState({ open: false, booking: null });
  const [vendorId, setVendorId] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [notes, setNotes] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, vendorsRes] = await Promise.all([
        getAllRides(),
        getAllVendors(),
      ]);
      const bks = bookingsRes.data.data || bookingsRes.data || [];
      setBookings(bks);
      setVendors((vendorsRes.data.data || []).filter((v) => v.isActive));

      const assignmentsMap = {};
      for (const b of bks) {
        try {
          const r = await getAssignmentForBooking(b.bookingId);
          if (r.data.data) {
            assignmentsMap[b.bookingId] = r.data.data;
          }
        } catch {
          // No assignment, ignore
        }
      }
      setAssignments(assignmentsMap);
    } catch (err) {
      onMessage("Failed to load: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const openAssign = (booking) => {
    setVendorId("");
    setPickupTime("");
    setNotes("");
    setAssignDialog({ open: true, booking });
  };

  const closeAssignDialog = () => {
    setAssignDialog({ open: false, booking: null });
    setVendorId("");
    setPickupTime("");
    setNotes("");
  };

  const handleAssign = async () => {
    if (!vendorId) {
      onMessage("Please select a vendor", "error");
      return;
    }

    setAssigning(true);
    try {
      await assignVendor(assignDialog.booking.bookingId, {
        vendorId: Number(vendorId),
        pickupTime: pickupTime || null,
        notes: notes || null,
      });
      onMessage("Assigned successfully");
      closeAssignDialog();
      loadData();
    } catch (err) {
      onMessage(err.response?.data?.message || "Assignment failed", "error");
    } finally {
      setAssigning(false);
    }
  };

  const handleCancelAssignment = async (assignmentId) => {
    if (!window.confirm("Cancel this assignment?")) return;

    try {
      await cancelAssignment(assignmentId);
      onMessage("Assignment cancelled");
      loadData();
    } catch (err) {
      onMessage(err.response?.data?.message || "Failed", "error");
    }
  };

  const matchingVendors = (booking) =>
    vendors.filter((v) => v.category === (booking?.category || "RIDE"));

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">All Bookings ({bookings.length})</Typography>
        <IconButton onClick={loadData}><RefreshIcon /></IconButton>
      </Box>

      {bookings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No bookings yet.</Typography>
        </Paper>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((b) => {
                const a = assignments[b.bookingId];
                return (
                  <TableRow key={b.bookingId}>
                    <TableCell>#{b.bookingId}</TableCell>
                    <TableCell>{b.name}</TableCell>
                    <TableCell><Chip label={b.category || "RIDE"} size="small" /></TableCell>
                    <TableCell>{b.fromDate?.split("T")[0]}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          b.isRideConfirmed === 0 ? "Pending" :
                          b.isRideConfirmed === 1 ? "Confirmed" :
                          b.isRideConfirmed === 2 ? "Cancelled" : "Completed"
                        }
                        size="small"
                        color={
                          b.isRideConfirmed === 0 ? "warning" :
                          b.isRideConfirmed === 1 ? "success" :
                          b.isRideConfirmed === 2 ? "error" : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {a ? (
                        <Box>
                          <Typography variant="body2">{a.vendor.vendorName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.pickupTime} | {a.status}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!a || a.status === "CANCELLED" ? (
                        <Button size="small" onClick={() => openAssign(b)}>
                          Assign
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleCancelAssignment(a.assignmentId)}
                        >
                          Unassign
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={assignDialog.open}
        onClose={closeAssignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Vendor — Booking #{assignDialog.booking?.bookingId}</DialogTitle>
        <DialogContent>
          {assignDialog.booking && (
            <Box mb={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                <strong>{assignDialog.booking.name}</strong> ({assignDialog.booking.category || "RIDE"})
                <br />
                {assignDialog.booking.homeAddress} → {assignDialog.booking.officeAddress}
              </Typography>
            </Box>
          )}

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Select Vendor</InputLabel>
            <Select
              value={vendorId}
              label="Select Vendor"
              onChange={(e) => setVendorId(e.target.value)}
            >
              {assignDialog.booking && matchingVendors(assignDialog.booking).length === 0 ? (
                <MenuItem disabled>
                  No active vendors for {assignDialog.booking.category || "RIDE"}
                </MenuItem>
              ) : (
                matchingVendors(assignDialog.booking).map((v) => (
                  <MenuItem key={v.vendorId} value={v.vendorId}>
                    {v.vendorName} ({v.vendorType}) — {v.contactNumber}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Pickup Time"
            type="time"
            value={pickupTime}
            onChange={(e) => setPickupTime(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog} disabled={assigning}>
            Cancel
          </Button>
          <Button onClick={handleAssign} variant="contained" disabled={assigning || !vendorId}>
            {assigning ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================
// TAB 3: STATUS UPDATE TESTING
// ============================================================
function StatusUpdateTab({ onMessage }) {
  const [bookingId, setBookingId] = useState("");
  const [assignment, setAssignment] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [loading, setLoading] = useState(false);

  // const lookupAssignment = async () => {
  //   if (!bookingId) {
  //     onMessage("Enter a booking ID", "error");
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     const r = await getAssignmentForBooking(bookingId);
  //     if (r.data.data) {
  //       setAssignment(r.data.data);
  //       onMessage("Assignment found");
  //     } else {
  //       setAssignment(null);
  //       onMessage("No assignment for this booking", "warning");
  //     }
  //   } catch (err) {
  //     setAssignment(null);
  //     onMessage(err.response?.data?.message || "Lookup failed", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const lookupAssignment = async () => {
  if (!bookingId) {
    onMessage("Enter a booking ID", "error");
    return;
  }

  setLoading(true);
  try {
    console.log("🔍 Looking up booking ID:", bookingId);
    const r = await getAssignmentForBooking(bookingId);
    console.log("🔍 Full response:", r);
    console.log("🔍 r.data:", r.data);
    console.log("🔍 r.data.data:", r.data.data);
    
    if (r.data.data) {
      setAssignment(r.data.data);
      onMessage("Assignment found");
    } else {
      setAssignment(null);
      onMessage("No assignment for this booking", "warning");
    }
  } catch (err) {
    console.log("❌ Error:", err);
    console.log("❌ err.response:", err.response);
    setAssignment(null);
    onMessage(err.response?.data?.message || "Lookup failed", "error");
  } finally {
    setLoading(false);
  }
};

  const handleUpdateStatus = async () => {
    if (!assignment || !newStatus) return;

    try {
      await updateAssignmentStatus(assignment.assignmentId, newStatus);
      onMessage(`Status updated to ${newStatus}`);
      lookupAssignment();
    } catch (err) {
      onMessage(err.response?.data?.message || "Update failed", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Test Assignment Status Updates
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="flex-end">
          <TextField
            label="Booking ID"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value.replace(/\D/g, ""))}
            sx={{ width: 200 }}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
          <Button variant="contained" onClick={lookupAssignment} disabled={loading}>
            {loading ? "Loading..." : "Lookup"}
          </Button>
        </Box>
      </Paper>

      {assignment && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Current Assignment
          </Typography>
          <Box display="grid" gridTemplateColumns="auto 1fr" gap={1} mb={3}>
            <Typography variant="body2"><strong>ID:</strong></Typography>
            <Typography variant="body2">{assignment.assignmentId}</Typography>

            <Typography variant="body2"><strong>Vendor:</strong></Typography>
            <Typography variant="body2">{assignment.vendor.vendorName}</Typography>

            <Typography variant="body2"><strong>Pickup:</strong></Typography>
            <Typography variant="body2">{assignment.pickupTime || "—"}</Typography>

            <Typography variant="body2"><strong>Status:</strong></Typography>
            <Box>
              <Chip
                label={assignment.status}
                color={
                  assignment.status === "COMPLETED" ? "success" :
                  assignment.status === "CANCELLED" ? "error" : "primary"
                }
                size="small"
              />
            </Box>

            <Typography variant="body2"><strong>Notes:</strong></Typography>
            <Typography variant="body2">{assignment.notes || "—"}</Typography>
          </Box>

          <Box display="flex" gap={2} alignItems="flex-end">
            <FormControl sx={{ width: 200 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleUpdateStatus}
              disabled={!newStatus}
            >
              Update
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}